/**
 * v3.15-1141 — Ghost Contact Awareness Modal.
 *
 * Forces every rep to read the activity digest BEFORE working a client record.
 *
 * Strict behaviour (per user PRD):
 *   - Fires every single time a client record opens (every mount, no session suppression).
 *   - No X button. No ESC. No overlay click-out. No "don't show again".
 *   - User must click "I Acknowledge — Continue".
 *   - If last call was < 15 minutes ago, a 2nd-step confirmation is required
 *     ("Continue Anyway" / "Go Back").
 *   - Audit log written every time the modal is shown.
 *
 * Color rules:
 *   RED    = last call < 15m ago
 *   YELLOW = last call < 60m ago
 *   GREEN  = last call >= 60m ago OR no call recorded
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../lib/api';
import { Button } from './ui/button';
import { Loader2, PhoneCall, PhoneIncoming, MessageSquare, StickyNote, ListChecks, CalendarClock, History, AlertTriangle, ShieldCheck, UserCircle2 } from 'lucide-react';

// v3.15-1143: Friendly labels for raw activity_type strings stored in Mongo.
// Anything not in this map falls back to a Title-Case rendering of the slug.
const ACTIVITY_LABELS = {
  call:                          'Call',
  call_made:                     'Outbound Call',
  call_received:                 'Inbound Call',
  voicemail:                     'Left Voicemail',
  voicemail_left:                'Left Voicemail',
  voicemail_received:            'Voicemail Received',
  sms_sent:                      'SMS Sent',
  sms_received:                  'SMS Received',
  email_sent:                    'Email Sent',
  email_received:                'Email Received',
  note_added:                    'Note Added',
  note:                          'Note',
  client_note:                   'Note',
  task_created:                  'Task Created',
  task_completed:                'Task Completed',
  appointment_scheduled:         'Appointment Scheduled',
  appointment_completed:         'Appointment Completed',
  appointment_missed:            'Appointment Missed',
  appointment_rescheduled:       'Appointment Rescheduled',
  appointment_cancelled:         'Appointment Cancelled',
  stage_changed:                 'Stage Changed',
  status_changed:                'Status Changed',
  ghost_contact_awareness_shown: 'Awareness Modal Shown',
  client_created:                'Client Created',
  client_updated:                'Client Updated',
};

const activityLabel = (raw) => {
  if (!raw) return 'Activity';
  const k = String(raw).toLowerCase();
  if (ACTIVITY_LABELS[k]) return ACTIVITY_LABELS[k];
  return k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};


const TONE = {
  red: {
    band:   'bg-red-600 text-white',
    panel:  'border-red-300 bg-red-50',
    badge:  'bg-red-100 text-red-800 border border-red-300',
    accent: 'text-red-700',
  },
  yellow: {
    band:   'bg-amber-500 text-slate-900',
    panel:  'border-amber-300 bg-amber-50',
    badge:  'bg-amber-100 text-amber-900 border border-amber-300',
    accent: 'text-amber-800',
  },
  green: {
    band:   'bg-emerald-600 text-white',
    panel:  'border-emerald-200 bg-emerald-50',
    badge:  'bg-emerald-100 text-emerald-800 border border-emerald-300',
    accent: 'text-emerald-700',
  },
};

// v3.18-1307 — Ghost is a live-collision system.  Ago strings only
// speak for < 15 minutes; everything older is operationally irrelevant
// and returns an empty string so the section can be hidden.
const fmtAgo = (mins) => {
  if (mins == null || mins >= 15) return '';
  if (mins < 1) return 'just now';
  return `${mins} minute${mins === 1 ? '' : 's'} ago`;
};

// v3.15-1143: minutes-since-ISO-timestamp for timeline rows that arrive
// without a precomputed minutes_ago value.
const minutesSince = (iso) => {
  if (!iso) return null;
  try {
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return null;
    return Math.max(0, Math.floor((Date.now() - t.getTime()) / 60000));
  } catch (_) { return null; }
};

const fmtUntil = (mins) => {
  if (mins == null) return '—';
  if (mins < 60) return `in ${mins} minute${mins === 1 ? '' : 's'}`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `in ${h}h ${m}m` : `in ${h}h`;
  const d = Math.floor(h / 24);
  return `in ${d} day${d === 1 ? '' : 's'}`;
};

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="border border-slate-200 rounded-lg bg-white p-3" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2 text-slate-800 font-semibold text-sm">
      <Icon className="h-4 w-4 text-slate-600" />
      <span>{title}</span>
    </div>
    <div className="text-sm text-slate-700">{children}</div>
  </div>
);

const GhostContactAwarenessModal = ({ clientId, onAcknowledge }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [confirmHotContact, setConfirmHotContact] = useState(false);
  const [busyAck, setBusyAck] = useState(false);

  // Lock scroll + block ESC while modal is open.
  // v3.15-1141A: also strip any externally-applied aria-hidden /
  // data-aria-hidden on our root (Radix `hideOthers` may stamp siblings),
  // neutralize stale Radix DialogOverlays, AND restore body pointer-events
  // (Radix RemoveScroll sets body.style.pointer-events='none' which
  // otherwise inherits into the modal).
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevPointerEvents = document.body.style.pointerEvents;
    document.body.style.overflow = 'hidden';
    const blockEsc = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', blockEsc, true);

    const strip = () => {
      const root = document.querySelector('[data-testid="ghost-contact-awareness-modal"]');
      if (!root) return;
      if (root.getAttribute('aria-hidden') === 'true') root.removeAttribute('aria-hidden');
      if (root.hasAttribute('data-aria-hidden')) root.removeAttribute('data-aria-hidden');
      // Restore body pointer-events if a sibling Radix dialog locked it.
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = 'auto';
      }
      // Neutralize stale sibling Radix shadcn DialogOverlays that survived
      // nav-cycle close animations (first-visit failure mode).
      document.querySelectorAll(
        'div.fixed.inset-0.bg-black\\/80, [data-radix-portal] > div[aria-hidden="true"][data-state]'
      ).forEach((el) => {
        if (el.style.pointerEvents === 'none') return;  // idempotent
        try {
          el.style.pointerEvents = 'none';
        } catch (_) { /* read-only style */ }
      });
    };
    const observer = new MutationObserver(strip);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'data-aria-hidden', 'data-state', 'style'],
    });
    strip();

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.pointerEvents = prevPointerEvents;
      document.removeEventListener('keydown', blockEsc, true);
      observer.disconnect();
    };
  }, []);

  // Load digest + audit-log the show event (every mount).
  // v3.15-1141A: surface audit failures (server logs + diagnostics) but
  // NEVER block client access.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/clients/${clientId}/ghost-awareness`);
        if (cancelled) return;
        setData(res.data);
        // Audit POST: server returns 200 always; `audited:false` means it
        // wrote a row to ghost_audit_failures instead. Either way we don't
        // block the user — we just surface the failure to console.
        api.post(`/clients/${clientId}/ghost-awareness/shown`)
          .then((r) => {
            if (r?.data && r.data.audited === false) {
              console.error(
                '[v3.15-1141A] Ghost audit write failed (server-side). error_id=',
                r.data.error_id, 'client_id=', clientId
              );
            }
          })
          .catch((err) => {
            // Network / 5xx — surface but never block.
            console.error(
              '[v3.15-1141A] Ghost audit POST failed (transport). client_id=',
              clientId, 'err=', err?.message || err
            );
          });
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load awareness digest.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const banner    = data?.banner || { level: 'green', text: 'This client is available.', icon: '🟢' };
  const tone      = TONE[banner.level] || TONE.green;
  const canonical = data?.canonical_last_communication || null;   // last-15-min only
  const activeCall = !!data?.active_call;
  const lastCall  = data?.last_call || null;                       // backend already caps at 15 min
  const lastIncomingCall = data?.last_incoming_call || null;
  const openTasks = data?.open_tasks?.items || [];
  const upcoming  = data?.upcoming_appointment || null;

  // v3.18-1307 — Any activity in the last 15 min is a collision hot contact.
  const isHotContact = activeCall || (
    canonical && canonical.minutes_ago != null && canonical.minutes_ago < 15
  );

  const onClickAcknowledge = () => {
    if (isHotContact && !confirmHotContact) {
      setConfirmHotContact(true);
      return;
    }
    setBusyAck(true);
    onAcknowledge?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4"
      style={{ zIndex: 100001, pointerEvents: 'auto' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ghost-modal-title"
      aria-describedby="ghost-modal-banner"
      data-testid="ghost-contact-awareness-modal"
      ref={(el) => {
        // v3.15-1141A: Radix Dialog's hideOthers() walks siblings and stamps
        // aria-hidden=true on us, making the modal inert for Playwright and
        // screen-readers. We portal to body (above) AND strip the attr if
        // anything sneaks it back on.
        if (!el) return;
        if (el.getAttribute('aria-hidden') === 'true') el.removeAttribute('aria-hidden');
        if (el.hasAttribute('data-aria-hidden')) el.removeAttribute('data-aria-hidden');
      }}
    >
      <div className="w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-xl shadow-2xl bg-slate-50 flex flex-col">
        {/* Banner — v3.18-1303 SSOT semantic label */}
        <div className={`px-5 py-4 ${tone.band}`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl leading-none shrink-0" aria-hidden="true" data-testid="ghost-banner-icon">{banner.icon || '🟢'}</span>
            <ShieldCheck className="h-6 w-6 shrink-0" />
            <div className="flex-1">
              <div id="ghost-modal-title" className="text-xs uppercase tracking-wider opacity-80">
                Ghost Contact Awareness · v3.18-1307
              </div>
              <div id="ghost-modal-banner" className="font-semibold text-base leading-tight" data-testid="ghost-banner-text">
                {banner.text}
              </div>
              {/* v3.18-1307 — Attribution subline.  Only shown for RED/YELLOW.
                  GREEN suppresses it entirely so the header speaks in
                  one sentence. */}
              {banner.level !== 'green' && canonical?.user_name && (
                <div className="text-[11px] opacity-90 mt-0.5" data-testid="ghost-banner-attribution">
                  {canonical.user_name}
                  {canonical.humanized ? ` · ${canonical.humanized}` : ''}
                </div>
              )}
            </div>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${tone.badge}`}
                  data-testid={`ghost-banner-level-${banner.level}`}>
              {banner.level}
            </span>
          </div>
          {data?.client && (
            <div className="mt-2 text-sm opacity-95">
              <span className="font-semibold">{data.client.full_name}</span>
              {data.client.assigned_rep_name && (
                <> · Rep: <span className="font-medium">{data.client.assigned_rep_name}</span></>
              )}
              {data.client.company && <> · {data.client.company}</>}
              {data.client.status && <> · {data.client.status}</>}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12 text-slate-500"
                 data-testid="ghost-modal-loading">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading client activity…
            </div>
          )}

          {error && !loading && (
            <div className="border border-red-300 bg-red-50 text-red-800 rounded p-3 text-sm"
                 data-testid="ghost-modal-error">
              {error}
            </div>
          )}

          {!loading && data && (
            <>
              {/* v3.18-1307 — GREEN inline explainer.  When there is no
                  operator activity in the last 15 min, the banner alone
                  is enough — but for full clarity we render a small
                  reassuring note inside the body so operators know the
                  file is safe to work. */}
              {banner.level === 'green' && (
                <div
                  className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 p-4 text-sm"
                  data-testid="ghost-green-explainer"
                >
                  No recent operator activity. You are clear to work this client.
                </div>
              )}

              {/* v3.18-1307 — Last Outbound Call.  Backend caps at 15 min:
                  older calls arrive as null and this block is hidden. */}
              {lastCall && (
                <Section icon={PhoneCall} title="Last Outbound Call" testId="ghost-section-last-call">
                  <div className={`rounded p-3 border ${tone.panel}`}>
                    <div className="font-medium">
                      <span data-testid="ghost-last-call-minutes-ago">
                        {fmtAgo(lastCall.minutes_ago)}
                      </span>
                      {lastCall.user_name && (
                        <> by <span className="font-semibold" data-testid="ghost-last-call-user">
                          {lastCall.user_name}
                        </span></>
                      )}
                    </div>
                    {(lastCall.outcome || lastCall.duration_sec != null) && (
                      <div className="text-xs text-slate-600 mt-1">
                        {lastCall.duration_sec != null && <>Duration: {lastCall.duration_sec}s · </>}
                        {lastCall.outcome && <>Outcome: {lastCall.outcome}</>}
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* v3.18-1307 — Last Incoming Call.  Backend caps at 15 min. */}
              {lastIncomingCall && (
                <Section icon={PhoneIncoming} title="Last Incoming Call" testId="ghost-section-last-incoming-call">
                  <div className="rounded p-3 border border-red-300 bg-red-50">
                    <div className="font-medium">
                      <span data-testid="ghost-last-incoming-call-minutes-ago">
                        {fmtAgo(lastIncomingCall.minutes_ago)}
                      </span>
                      {lastIncomingCall.answered_by_user_name ? (
                        <> · answered by <span className="font-semibold"
                              data-testid="ghost-last-incoming-call-user">
                          {lastIncomingCall.answered_by_user_name}
                        </span></>
                      ) : (
                        <> · <span className="italic text-slate-500">no one picked up</span></>
                      )}
                    </div>
                    {(lastIncomingCall.disposition || lastIncomingCall.duration_sec != null) && (
                      <div className="text-xs text-slate-600 mt-1">
                        {lastIncomingCall.duration_sec != null && <>Duration: {lastIncomingCall.duration_sec}s · </>}
                        {lastIncomingCall.disposition && <>Disposition: {lastIncomingCall.disposition}</>}
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* v3.18-1307 — Open Tasks kept: they are actionable and
                  help the operator decide next steps, not history. */}
              {openTasks.length > 0 && (
                <Section icon={ListChecks} title={`Open Tasks (${data.open_tasks?.count ?? 0})`}
                         testId="ghost-section-open-tasks">
                  <ul className="space-y-1">
                    {openTasks.slice(0, 10).map((t) => (
                      <li key={t.id} className="flex items-center justify-between text-sm"
                          data-testid={`ghost-task-${t.id}`}>
                        <span className="text-slate-800">{t.title || '(untitled task)'}</span>
                        <span className="text-xs text-slate-500 ml-2">
                          {t.status}{t.due_at ? ` · due ${t.due_at}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* v3.18-1307 — Upcoming Appointment kept: forward-looking
                  operational context (not communication history). */}
              {upcoming && (
                <Section icon={CalendarClock} title="Upcoming Appointment"
                         testId="ghost-section-upcoming">
                  <div className={`rounded p-3 border ${upcoming.within_24h ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="font-medium">
                      {upcoming.date_time_start} {upcoming.minutes_until != null && (
                        <span className="text-xs text-slate-600">({fmtUntil(upcoming.minutes_until)})</span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      {upcoming.assigned_name && <>With {upcoming.assigned_name} · </>}
                      Status: {upcoming.status}
                      {upcoming.within_24h && (
                        <span className="ml-2 text-amber-700 font-semibold" data-testid="ghost-appt-24h-flag">
                          ⏰ Within 24h
                        </span>
                      )}
                    </div>
                  </div>
                </Section>
              )}

              {/* v3.18-1307 — Recent Timeline / Last SMS / Last Email /
                  Last Voicemail / Last Note / Last Activity hero all
                  removed.  Ghost is not a communication-history viewer;
                  operators have the Communications tab for that. */}
            </>
          )}
        </div>

        {/* Footer / Acknowledge */}
        <div className="border-t border-slate-200 bg-white px-5 py-4">
          {isHotContact && confirmHotContact ? (
            <div className="space-y-3" data-testid="ghost-hot-contact-confirm">
              <div className="flex items-start gap-2 text-red-800 bg-red-50 border border-red-300 rounded p-3">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-semibold">
                    ⚠ Another representative is currently working this client
                    {canonical?.user_name ? <> ({canonical.user_name}{canonical.humanized ? `, ${canonical.humanized}` : ''})</> : null}.
                  </div>
                  <div className="text-xs mt-1">
                    Are you sure you want to work this file right now? This is the exact scenario Ghost is designed to prevent.
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setConfirmHotContact(false)}
                  data-testid="ghost-go-back-btn"
                >
                  Go Back
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => { setBusyAck(true); onAcknowledge?.(); }}
                  disabled={busyAck}
                  data-testid="ghost-continue-anyway-btn"
                >
                  {busyAck ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Continue Anyway
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className={`text-xs ${tone.accent}`}>
                Acknowledgement is logged. Ghost will fire again on every record open.
              </div>
              <Button
                className={banner.level === 'red'
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : banner.level === 'yellow'
                  ? 'bg-amber-500 hover:bg-amber-600 text-slate-900'
                  : 'bg-slate-900 hover:bg-slate-800 text-white'}
                disabled={loading || busyAck}
                onClick={onClickAcknowledge}
                data-testid="ghost-acknowledge-btn"
              >
                {busyAck ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                I Acknowledge — Continue
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GhostContactAwarenessModal;
